import {
  faChevronDown,
  faChevronRight,
  faLock,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import ContractCoverageIndicator from '@/components/ContractCoverageIndicator';
import ContractInsuranceEntitiesPanel from '@/components/ContractInsuranceEntitiesPanel';
import { CONTRACTS_PATH } from '@/constants/contracts';
import ContractsGridContext from '@/contexts/contractsGridContext';
import { Pill, Tooltip, Typography } from '@/ui';
import { formatContractMonth } from '@/utils/contractLabels';

import './styles.css';
import type { Props } from './types';

const CompactContractsList: FC<Props> = ({ contracts }) => {
  const navigate = useNavigate();
  const { expandedIds, filterOptions, toggleExpanded } = useContext(ContractsGridContext);

  return (
    <div className="text-sm">
      <div className="hidden grid-cols-[minmax(0,3fr)_minmax(0,2fr)] border-b border-uniform-gray-200 bg-uniform-gray-50 font-semibold text-uniform-gray-600 sm:grid dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
        <span className="px-4 py-3">Contract</span>
        <span className="border-l border-uniform-gray-200 px-4 py-3 dark:border-uniform-gray-700">
          Drug categories
        </span>
      </div>
      {contracts.map((contract) => {
        const expanded = expandedIds.has(contract.id);
        const statusColor =
          contract.status === 'active'
            ? 'primary'
            : contract.status === 'upcoming'
              ? 'secondary'
              : 'neutral';
        return (
          <div
            key={contract.id}
            className="border-b border-uniform-gray-200 last:border-b-0 dark:border-uniform-gray-700">
            <div
              role="link"
              tabIndex={0}
              aria-label={`Open ${contract.name}`}
              className="grid cursor-pointer sm:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]"
              onClick={(event) => {
                if (
                  !(event.target as HTMLElement).closest(
                    'button, a, [role="button"], span[tabindex="0"]'
                  )
                )
                  navigate(`${CONTRACTS_PATH}/${contract.id}`);
              }}
              onKeyDown={(event) => {
                if (event.target === event.currentTarget && event.key === 'Enter')
                  navigate(`${CONTRACTS_PATH}/${contract.id}`);
              }}>
              <div className="flex min-w-0 gap-3 p-4">
                <button
                  type="button"
                  aria-label={expanded ? 'Collapse contract' : 'Expand contract'}
                  aria-expanded={expanded}
                  className="self-start text-uniform-gray-500"
                  onClick={() => toggleExpanded(contract.id)}>
                  <FontAwesomeIcon icon={expanded ? faChevronDown : faChevronRight} />
                </button>
                <div className="min-w-0 space-y-1">
                  <div className="font-semibold">
                    <Link to={`${CONTRACTS_PATH}/${contract.id}`} className="hover:underline">
                      {contract.name}
                    </Link>
                    {contract.locked_at && (
                      <FontAwesomeIcon
                        icon={faLock}
                        title="Locked"
                        className="ml-2 text-xs text-uniform-gray-500"
                      />
                    )}
                    {contract.conflicts.length > 0 && (
                      <Tooltip
                        content={contract.conflicts.map((conflict) => (
                          <Typography
                            key={`${conflict.contract_id}-${conflict.insurance_provider_name}-${conflict.insurance_type}-${conflict.jurisdiction_abbreviation}`}
                            className="text-inherit dark:text-inherit">
                            Contracted twice: {conflict.insurance_provider_name} ·{' '}
                            {conflict.insurance_type} · {conflict.jurisdiction_abbreviation}. Also
                            covered by {conflict.contract_name}.
                          </Typography>
                        ))}
                        focusable>
                        <FontAwesomeIcon
                          icon={faTriangleExclamation}
                          aria-label="Contracted twice"
                          className="ml-2 text-orange-600"
                        />
                      </Tooltip>
                    )}
                  </div>
                  {contract.organization_name && (
                    <Typography muted>{contract.organization_name}</Typography>
                  )}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-uniform-gray-500">
                    <Pill size="sm" variant="subtle" color={statusColor}>
                      {contract.status[0].toUpperCase() + contract.status.slice(1)}
                    </Pill>
                    <span>
                      v{contract.version} of {contract.version_count}
                    </span>
                    <span>
                      {formatContractMonth(contract.start_at)} →{' '}
                      {contract.end_at ? formatContractMonth(contract.end_at) : 'open'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-uniform-gray-200 px-4 py-3 sm:border-t-0 sm:border-l dark:border-uniform-gray-700">
                {(filterOptions?.drug_categories ?? []).map((category) => (
                  <div
                    key={category.value}
                    className="flex min-w-0 items-center justify-between gap-2">
                    <span className="truncate" title={category.label}>
                      {category.label}
                    </span>
                    <Tooltip
                      content={
                        <div>
                          <Typography className="text-inherit dark:text-inherit">
                            {category.label} — insurance entities covering each family:
                          </Typography>
                          {(contract.category_coverage[category.value]?.families ?? []).map(
                            (family) => (
                              <Typography
                                key={family.program_family_id}
                                className="text-inherit dark:text-inherit">
                                {family.program_family_name}:{' '}
                                {family.fully_covered_insurance_entities}/
                                {contract.insurance_entity_count}
                                {family.covered_insurance_entities >
                                  family.fully_covered_insurance_entities &&
                                  ` (${family.covered_insurance_entities - family.fully_covered_insurance_entities} partly)`}
                              </Typography>
                            )
                          )}
                        </div>
                      }
                      focusable>
                      <ContractCoverageIndicator
                        coverage={contract.category_coverage[category.value]?.coverage ?? 'none'}
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
            </div>
            {expanded && (
              <div className="bg-uniform-gray-50 dark:bg-uniform-gray-800">
                <ContractInsuranceEntitiesPanel
                  contract={contract}
                  filterOptions={filterOptions}
                  compact
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CompactContractsList;
